"use client";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import TableSortingIcon from "@/components/common/TableSortingIcon";
import { OrderSet } from "@/types/models/order";
import { ColumnDef } from "@tanstack/react-table";
import { format } from "date-fns";
import { formatCurrency } from "@/utils/currency";

const formatShippingStatus = (status?: string) => {
  if (!status) return "Not Set";
  return status
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const getShippingVariant = (status?: string) => {
  if (!status) return "neutral";

  if (["DELIVERED", "RTO_DELIVERED"].includes(status)) return "delivered";
  if (["CANCELLED", "REJECTED", "RTO_FAILED"].includes(status)) {
    return "cancelled";
  }
  if (
    ["DISPATCHED", "PICKED_UP", "IN_TRANSIT", "OUT_FOR_DELIVERY"].includes(
      status,
    )
  ) {
    return "info";
  }
  if (["PENDING", "UNDER_CONFIRMATION", "PROCESSING"].includes(status)) {
    return "warning";
  }

  return "neutral";
};

export const columns: ColumnDef<OrderSet>[] = [
  {
    accessorKey: "id",
    size: 180,
    minSize: 160,
    maxSize: 220,
    header: ({ column }) => (
      <Button
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        variant="ghost"
        className="w-full flex p-0 items-center justify-between hover:bg-transparent! hover:text-inherit group"
      >
        <span className="text-base font-bold">Order Set ID</span>
        <div
          className={`transition-opacity duration-200 ${
            column.getIsSorted()
              ? "opacity-100"
              : "opacity-0 group-hover:opacity-100"
          }`}
        >
          <TableSortingIcon sortDirection={column.getIsSorted()} />
        </div>
      </Button>
    ),
    cell: ({ row }) => {
      const setId = row.original.id;
      return <span className="text-xs font-semibold">#{setId.slice(0, 8)}</span>;
    },
  },
  {
    accessorKey: "order_id",
    size: 180,
    minSize: 160,
    maxSize: 220,
    header: ({ column }) => (
      <Button
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        variant="ghost"
        className="w-full flex p-0 items-center justify-between hover:bg-transparent! hover:text-inherit group"
      >
        <span className="text-base font-bold">Order ID</span>
        <div
          className={`transition-opacity duration-200 ${
            column.getIsSorted()
              ? "opacity-100"
              : "opacity-0 group-hover:opacity-100"
          }`}
        >
          <TableSortingIcon sortDirection={column.getIsSorted()} />
        </div>
      </Button>
    ),
    cell: ({ row }) => {
      const orderId = row.original.order_id;
      return <span className="text-xs font-semibold">#{orderId.slice(0, 8)}</span>;
    },
  },
  {
    accessorKey: "created_at",
    size: 160,
    minSize: 140,
    maxSize: 200,
    header: ({ column }) => (
      <Button
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        variant="ghost"
        className="w-full flex p-0 items-center justify-between hover:bg-transparent! hover:text-inherit group"
      >
        <span className="text-base font-bold">Created</span>
        <div
          className={`transition-opacity duration-200 ${
            column.getIsSorted()
              ? "opacity-100"
              : "opacity-0 group-hover:opacity-100"
          }`}
        >
          <TableSortingIcon sortDirection={column.getIsSorted()} />
        </div>
      </Button>
    ),
    cell: ({ row }) => {
      const createdAt = row.original.created_at;
      return (
        <span className="text-xs text-muted-foreground">
          {format(new Date(createdAt), "MMM dd, yyyy")}
        </span>
      );
    },
  },
  {
    accessorKey: "items",
    size: 130,
    minSize: 110,
    maxSize: 160,
    header: () => <span className="text-base font-bold">Items</span>,
    cell: ({ row }) => {
      const itemCount = row.original.items?.length || 0;
      const totalQuantity = row.original.calculation?.total_quantity || 0;

      return (
        <div>
          <h3 className="text-xs font-semibold">{itemCount} items</h3>
          <span className="text-xs text-muted-foreground">{totalQuantity} units</span>
        </div>
      );
    },
  },
  {
    accessorKey: "calculation",
    size: 120,
    minSize: 100,
    maxSize: 160,
    header: () => <span className="text-base font-bold">Amount</span>,
    cell: ({ row }) => {
      const total = row.original.calculation?.grand_total_int || 0;
      return <span className="text-xs font-semibold">{formatCurrency(total)}</span>;
    },
  },
  {
    accessorKey: "current_shipping_stage",
    size: 180,
    minSize: 160,
    maxSize: 220,
    header: () => <span className="text-base font-bold">Shipping Status</span>,
    cell: ({ row }) => {
      const status = row.original.current_shipping_stage;
      return (
        <Badge variant={getShippingVariant(status) as any}>
          {formatShippingStatus(status)}
        </Badge>
      );
    },
  },
];
